/**
 * `deployments.json` — the wire shape of the published-deployments manifest.
 *
 * **Web-only — there is no Unity counterpart.** The desktop client ships one
 * build at a time to one installer; the web port can host the default build at
 * `https://a1.campfire3d.com/` and any number of named channels at
 * `https://a1.campfire3d.com/<channel>/` simultaneously (and under every alias
 * the host answers to — see `scripts/deploy-target.sh`). This file is
 * how a running build learns about its siblings.
 *
 * ## Who writes it
 *
 * `scripts/deploy-client-dev.sh`, server-side, after activating a release. Each
 * release carries its own one-line `campfire-release.json` (an entry of exactly
 * the shape below), and the activation step concatenates the `current` release
 * of the root deployment and of every channel into this document. It is
 * therefore DERIVED from what is actually on disk — a removed channel drops out
 * on the next deploy rather than lingering as a stale row.
 *
 * ## Where it lives
 *
 * `https://<origin>/deployments.json`, at the ORIGIN ROOT — deliberately not
 * under the running build's base. Every deployment on the host shares one
 * manifest, so a channel build must NOT resolve this through `publicUrl()`.
 * The name has no leading dot on purpose: nginx denies `location ~ /\.`.
 *
 * ## Tolerance
 *
 * Parsing is defensive in both directions. Unknown fields are ignored (a newer
 * deploy script may add some), and a row missing anything but `basePath` is
 * kept with empty strings — a half-described deployment is still a deployment
 * the reader can navigate to, and dropping it would under-report what is
 * published, which is the one thing this list exists to get right.
 */

/** One published deployment. Mirrors a release's `campfire-release.json`. */
export interface DeploymentEntryDto {
  /** Channel name; `""` for the default root deployment. */
  readonly channel: string;
  /** URL path the build is served from — `"/"` or `"/<channel>/"`. */
  readonly basePath: string;
  /** Release directory name (`<utc-timestamp>-<sha>[-dirty]`). */
  readonly releaseId: string;
  /** Short commit SHA the release was built from. */
  readonly commit: string;
  /** Git branch at build time. */
  readonly branch: string;
  /** True when the build included uncommitted working-tree changes. */
  readonly dirty: boolean;
  /** ISO-8601 UTC timestamp of the deploy. */
  readonly deployedAt: string;
  /** Local username of whoever ran the deploy. */
  readonly deployedBy: string;
}

export interface DeploymentsManifestDto {
  readonly schemaVersion: number;
  readonly generatedAt: string;
  readonly deployments: readonly DeploymentEntryDto[];
}

/** The manifest URL. Origin-absolute — see the "Where it lives" note above. */
export const K_DEPLOYMENTS_MANIFEST_URL = "/deployments.json";

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

function readString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Normalizes a base path to Vite's `BASE_URL` spelling — leading AND trailing
 * slash — so `basePath` can be compared against `import.meta.env.BASE_URL` with
 * `===` to answer "is this the build I am looking at?".
 */
function normalizeBasePath(value: unknown): string {
  const raw = readString(value).trim();
  if (!raw || raw === "/") return "/";
  const withLead = raw.startsWith("/") ? raw : `/${raw}`;
  return withLead.endsWith("/") ? withLead : `${withLead}/`;
}

function parseEntry(value: unknown): DeploymentEntryDto | null {
  const row = asRecord(value);
  const basePath = normalizeBasePath(row.basePath);
  // A row with no usable path cannot be linked to or matched against the
  // running build, which is all this list does — that one IS worth dropping.
  if (!basePath) return null;
  return {
    channel: readString(row.channel),
    basePath,
    releaseId: readString(row.releaseId),
    commit: readString(row.commit),
    branch: readString(row.branch),
    dirty: row.dirty === true,
    deployedAt: readString(row.deployedAt),
    deployedBy: readString(row.deployedBy),
  };
}

/** Parses a fetched manifest, tolerating unknown fields and partial rows. */
export function parseDeploymentsManifest(value: unknown): DeploymentsManifestDto {
  const root = asRecord(value);
  const rows = Array.isArray(root.deployments) ? root.deployments : [];
  const deployments: DeploymentEntryDto[] = [];
  for (const row of rows) {
    const parsed = parseEntry(row);
    if (parsed) deployments.push(parsed);
  }
  // Default deployment first, then channels newest-deploy-first — the order the
  // list is read in, decided here so every consumer agrees.
  deployments.sort((a, b) => {
    if (!a.channel !== !b.channel) return a.channel ? 1 : -1;
    return b.deployedAt.localeCompare(a.deployedAt);
  });
  return {
    schemaVersion: typeof root.schemaVersion === "number" ? root.schemaVersion : 0,
    generatedAt: readString(root.generatedAt),
    deployments,
  };
}
